import React, { useEffect } from 'react'
import { useFormikContext } from 'formik'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'

import SimpleInput from 'app/componentV4/Input/SimpleInput'
import LabelInput from 'app/componentV4/Labels/LabelInput'
import { makeStyles, useMediaQuery } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import ContainedButton from 'app/componentV4/Button/ContainedButton'
import { CLOSE, SECONDARY, CANCEL } from 'app/constants/button'
import {
  openWarningDialog,
  closeWarningDialog,
} from 'app/store/actions/warningDialog.actions'

import clsx from 'clsx'

const useStyles = makeStyles((theme) => ({
  formRoot: {
    '& .MuiFormControl-root': {
      '& .label-error': {
        marginTop: '4px',
      },
    },
  },
  inputFormat: {
    '& .MuiInputBase-root': {
      color: 'rgb(24 53 96 / var(--tw-text-opacity)) !important',
    },
    '& fieldset': {
      height: '53px !important',
      borderRadius: '.8rem !important',
    },
    '& .MuiOutlinedInput-input': {
      padding: '10px',
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    },
    '& .MuiInputLabel-outlined': {
      transform: 'translate(14px, 17px) scale(1)',
    },
  },
  labelInput: {
    '& span': {
      color: '#EF4444',
    },
  },
}))

const SocialInformationForm = ({
  subscriberId,
  onSocialChange,
  onSocialReset,
  refetchData,
}) => {
  const formik = useFormikContext()

  const classes = useStyles()
  const { formatMessage } = useIntl()
  const isButtonDisabled = !formik.dirty || formik.isValidating
  const isButtonLoading = formik.isValidating || formik.isSubmitting
  const theme = useTheme()
  const isDownXs = useMediaQuery(theme.breakpoints.down('xs'))
  const dispatch = useDispatch()

  const onCancelClick = () => {
    if (formik.dirty) {
      const payload = {
        title: formatMessage({ id: 'dialog.discard.title' }),
        description: formatMessage({ id: 'dialog.discard.body' }),
        submitText: formatMessage({
          id: 'draft.notification.create.popup.title',
        }),
        closeText: formatMessage({ id: 'dialog.discard.close' }),
        type: CANCEL,
        onSubmit: () => {
          formik.resetForm()
          onSocialReset()
          refetchData(subscriberId)
          dispatch(closeWarningDialog())
        },
        onClose: () => {
          dispatch(closeWarningDialog())
        },
      }
      dispatch(openWarningDialog(payload))
    }
  }

  useEffect(() => {
    if (formik.dirty) {
      onSocialChange(formik.dirty)
    } else {
      onSocialReset()
    }
  }, [formik.dirty])

  return (
    <div className={clsx('w-full', classes.formRoot)}>
      <div className={isDownXs ? 'space-x-0' : 'flex space-x-10'}>
        <LabelInput
          label={formatMessage({
            id: 'subscriber.label.facebookProfile',
          })}
          className="w-full capitalize"
        >
          <SimpleInput
            id="facebookProfile"
            name="facebookProfile"
            type="text"
            value={formik.values.facebookProfile}
            className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
            onChange={(event) => {
              formik.setFieldValue('facebookProfile', event.target.value)
              formik.setFieldTouched('facebookProfile', true, false)
            }}
            placeholder={formatMessage({
              id: 'subscriber.label.facebookProfile',
            })}
            error={
              formik.errors.facebookProfile && formik.touched.facebookProfile
            }
            helperText={formik.errors.facebookProfile}
          />
        </LabelInput>
        <LabelInput
          label={formatMessage({
            id: 'subscriber.label.twitterProfile',
          })}
          className="w-full capitalize"
        >
          <SimpleInput
            id="twitterProfile"
            name="twitterProfile"
            type="text"
            value={formik.values.twitterProfile}
            className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
            onChange={(e) => {
              formik.setFieldValue('twitterProfile', e.target.value)
              formik.setFieldTouched('twitterProfile', true, false)
            }}
            placeholder={formatMessage({
              id: 'subscriber.label.twitterProfile',
            })}
            error={
              formik.errors.twitterProfile && formik.touched.twitterProfile
            }
            helperText={formik.errors.twitterProfile}
          />
        </LabelInput>
      </div>
      <div className={isDownXs ? 'space-x-0' : 'flex space-x-10'}>
        <LabelInput
          label={formatMessage({
            id: 'subscriber.label.linkedinProfile',
          })}
          className="w-full capitalize"
        >
          <SimpleInput
            id="linkedinProfile"
            name="linkedinProfile"
            type="text"
            value={formik.values.linkedinProfile}
            className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
            onChange={(event) => {
              formik.setFieldValue('linkedinProfile', event.target.value)
              formik.setFieldTouched('linkedinProfile', true, false)
            }}
            placeholder={formatMessage({
              id: 'subscriber.label.linkedinProfile',
            })}
            error={
              formik.errors.linkedinProfile && formik.touched.linkedinProfile
            }
            helperText={formik.errors.linkedinProfile}
          />
        </LabelInput>
        <LabelInput
          label={formatMessage({
            id: 'subscriber.label.line',
          })}
          className="w-full uppercase"
        >
          <SimpleInput
            id="lineProfile"
            name="lineProfile"
            type="text"
            value={formik.values.lineProfile}
            className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
            onChange={(e) => {
              formik.setFieldValue('lineProfile', e.target.value)
              formik.setFieldTouched('lineProfile', true, false)
            }}
            placeholder={formatMessage({
              id: 'subscriber.label.line',
            })}
            error={formik.errors.lineProfile && formik.touched.lineProfile}
            helperText={formik.errors.lineProfile}
          />
        </LabelInput>
      </div>
      <div className={isDownXs ? 'space-x-0' : 'flex space-x-10'}>
        <LabelInput
          label={formatMessage({
            id: 'socialMediaIntegrations.provider.instagram.title',
          })}
          className="w-full capitalize"
        >
          <SimpleInput
            id="instagramProfile"
            name="instagramProfile"
            type="text"
            value={formik.values.instagramProfile}
            className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
            onChange={(e) => {
              formik.setFieldValue('instagramProfile', e.target.value)
              formik.setFieldTouched('instagramProfile', true, false)
            }}
            placeholder={formatMessage({
              id: 'socialMediaIntegrations.provider.instagram.title',
            })}
            error={
              formik.errors.instagramProfile && formik.touched.instagramProfile
            }
            helperText={formik.errors.instagramProfile}
          />
        </LabelInput>
        <LabelInput label="GG+" className="w-full capitalize">
          <SimpleInput
            id="ggProfile"
            name="ggProfile"
            type="text"
            value={formik.values.ggProfile}
            className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
            onChange={(e) => {
              formik.setFieldValue('ggProfile', e.target.value)
              formik.setFieldTouched('ggProfile', true, false)
            }}
            placeholder="GG+"
            error={formik.errors.ggProfile && formik.touched.ggProfile}
            helperText={formik.errors.ggProfile}
          />
        </LabelInput>
      </div>
      <div className={isDownXs ? 'space-x-0' : 'flex space-x-10'}>
        <LabelInput label="Youtube" className="w-full capitalize">
          <SimpleInput
            id="youtubeProfile"
            name="youtubeProfile"
            type="text"
            value={formik.values.youtubeProfile}
            className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
            onChange={(e) => {
              formik.setFieldValue('youtubeProfile', e.target.value)
              formik.setFieldTouched('youtubeProfile', true, false)
            }}
            placeholder="Youtube"
            error={
              formik.errors.youtubeProfile && formik.touched.youtubeProfile
            }
            helperText={formik.errors.youtubeProfile}
          />
        </LabelInput>
        <LabelInput
          label={formatMessage({
            id: 'label.others',
          })}
          className="w-full capitalize"
        >
          <SimpleInput
            id="otherProfile"
            name="otherProfile"
            type="text"
            value={formik.values.otherProfile}
            className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
            onChange={(e) => {
              formik.setFieldValue('otherProfile', e.target.value)
              formik.setFieldTouched('otherProfile', true, false)
            }}
            placeholder={formatMessage({
              id: 'label.others',
            })}
            error={formik.errors.otherProfile && formik.touched.otherProfile}
            helperText={formik.errors.otherProfile}
          />
        </LabelInput>
      </div>

      <div className="flex justify-end space-x-5 mt-5">
        <ContainedButton
          color={CLOSE}
          onClick={onCancelClick}
          disabled={isButtonDisabled || isButtonLoading}
          type="button"
          className="w-96"
        >
          <div className="flex">
            <span className="normal-case">
              {formatMessage({
                id: 'action.cancel',
              })}
            </span>
          </div>
        </ContainedButton>
        <ContainedButton
          color={SECONDARY}
          isLoading={isButtonLoading}
          disabled={isButtonDisabled || isButtonLoading}
          type="submit"
        >
          <div className="flex">
            <span className="normal-case">
              {formatMessage({
                id: 'subscriberCrm.editGroup.save.label',
              })}
            </span>
          </div>
        </ContainedButton>
      </div>
    </div>
  )
}

export default SocialInformationForm
